import categoryService from "../../../services/ticketService/categoryService";
import { CategoryInterface } from "../../../models/TicketModule/Category";
import { CACHE_KEY_CATEGORIES } from "../../../utils/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface DeleteCategoryContext {
  previousCategories: CategoryInterface[];
  deletedCategory: CategoryInterface | undefined;
}

const useDeleteCategory = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the category to delete
    DeleteCategoryContext
  >({
    mutationFn: categoryService.Remove, // Remove should take a string ID

    onMutate: (categoryId: string) => {
      const previousCategories =
        queryClient.getQueryData<CategoryInterface[]>([
          CACHE_KEY_CATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<CategoryInterface[]>(
        [
          CACHE_KEY_CATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousCategories.filter((category) => category.id !== categoryId) // Use categoryId to filter
      );

      return {
        previousCategories,
        deletedCategory: previousCategories.find(
          (category) => category.id === categoryId
        ),
      };
    },

    onError: (error, categoryId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<CategoryInterface[]>(
        [
          CACHE_KEY_CATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousCategories
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteCategory;
